import StockOpnameEntriesApiV2 from 'api/v2/admins/stock-opname-entries-api-v2'
import StockOpnamesApiV2 from 'api/v2/admins/stock-opnames-api-v2'
import RedirectButton from 'components/atoms/redirect-button'
import PaginationBar from 'components/organisms/pagination-bar/pagination-bar'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'
import { saveAs } from 'file-saver'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row, Spinner, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const ENTRY_SCOPE_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'cek_fisik', label: 'Need Cek Fisik' },
  { key: 'cek_kelengkapan', label: 'Need Cek Kelengkapan' }
]

const INTERNAL_CATEGORY_OPTIONS = [
  { key: 'H', label: 'H - Hermes' },
  { key: 'W', label: 'W - Watch' },
  { key: 'C', label: 'C - Curated' }
]

const booleanBadge = (value) => {
  return value
    ? <Badge color='success'>{ 'Yes' }</Badge>
    : <Badge color='secondary'>{ 'No' }</Badge>
}

const physicalCheckBadge = (entry) => {
  if (entry?.physical_check_status === 'found') return <Badge color='success'>{ '✓ Ada' }</Badge>
  if (entry?.physical_check_status === 'not_found') return <Badge color='danger'>{ '✕ Tidak Ada' }</Badge>

  return <Badge color='secondary'>{ 'Pending' }</Badge>
}

const normalizeInternalCategoryFilters = (value) => {
  const filters = (value || 'H,W,C')
    .toString()
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter((item) => [ 'H', 'W', 'C' ].includes(item))

  return filters.length ? filters : [ 'H', 'W', 'C' ]
}

const progressLabel = (completed, total) => {
  const safeCompleted = Number(completed || 0)
  const safeTotal = Number(total || 0)
  const percentage = safeTotal > 0 ? Math.min(100, Math.floor((safeCompleted / safeTotal) * 100)) : 0

  return `${safeCompleted}/${safeTotal} (${percentage}%)`
}

const resolveInitialEntryScope = () => {
  const params = new URLSearchParams(window.location.search)
  const scope = params.get('entry_scope')

  return ENTRY_SCOPE_OPTIONS.some((option) => option.key === scope) ? scope : 'all'
}

const resolveInitialEntryPage = () => {
  const params = new URLSearchParams(window.location.search)
  const page = Number(params.get('entry_page') || 1)

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const stockOpnameDetailPath = ({ id, query, entryScope, entryPage, returnTo }) => {
  const params = new URLSearchParams()

  if (query.trim()) params.set('q', query.trim())
  if (entryScope && entryScope !== 'all') params.set('entry_scope', entryScope)
  if (entryPage > 1) params.set('entry_page', String(entryPage))
  if (returnTo) params.set('return_to', returnTo)

  const search = params.toString()
  return `/app/stock-opnames/${id}${search ? `?${search}` : ''}`
}

const StockOpnameDetailPage = ({ pageUtils }) => {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const [ stockOpname, setStockOpname ] = useState(null)
  const [ stockOpnameEntries, setStockOpnameEntries ] = useState([])
  const [ entryPagination, setEntryPagination ] = useState({ current_page: 1, total_pages: 1, total_count: 0, per_page: 25 })
  const [ entryPage, setEntryPage ] = useState(resolveInitialEntryPage)
  const [ query, setQuery ] = useState(() => new URLSearchParams(window.location.search).get('q') || '')
  const [ entryScope, setEntryScope ] = useState(resolveInitialEntryScope)
  const [ isLoading, setIsLoading ] = useState(true)
  const [ isLoadingHeader, setIsLoadingHeader ] = useState(true)
  const latestStockOpnameRequestRef = useRef(0)
  const latestEntriesRequestRef = useRef(0)
  const [ headerValues, setHeaderValues ] = useState({ title: '', in_progress: false, target_date: '', internal_category_filter: 'H,W,C', auto_generate_entries: true })
  const [ skuText, setSkuText ] = useState('')
  const [ isSavingHeader, setIsSavingHeader ] = useState(false)
  const [ isAddingSku, setIsAddingSku ] = useState(false)
  const [ isGeneratingEntries, setIsGeneratingEntries ] = useState(false)

  useEffect(() => {
    if (stockOpname) {
      setHeaderValues({
        title                    : stockOpname.title || '',
        in_progress              : !!stockOpname.in_progress,
        target_date              : stockOpname.target_date || '',
        internal_category_filter : stockOpname.internal_category_filter || 'H,W,C',
        auto_generate_entries    : !!stockOpname.auto_generate_entries
      })
    }
  }, [ stockOpname ])

  const fetchStockOpname = () => {
    const requestId = latestStockOpnameRequestRef.current + 1
    latestStockOpnameRequestRef.current = requestId
    setIsLoadingHeader(true)

    StockOpnamesApiV2.show(id)
      .then((response) => {
        if (requestId !== latestStockOpnameRequestRef.current) return
        setStockOpname(response.data.stock_opname || null)
      })
      .catch((error) => {
        if (requestId !== latestStockOpnameRequestRef.current) return
        pageUtils.setApiErrorMsg(error.response?.data)
        pageUtils.setAlertMsg('Unable to fetch stock opname', ALERT_TYPES.DANGER)
      })
      .finally(() => {
        if (requestId !== latestStockOpnameRequestRef.current) return
        setIsLoadingHeader(false)
      })
  }

  const fetchEntries = (params = {}) => {
    const requestId = latestEntriesRequestRef.current + 1
    latestEntriesRequestRef.current = requestId
    setIsLoading(true)

    StockOpnameEntriesApiV2.get({ stock_opname_id: id, page: entryPage, per_page: 25, ...params })
      .then((response) => {
        if (requestId !== latestEntriesRequestRef.current) return
        setStockOpnameEntries(response.data.stock_opname_entries || [])
        setEntryPagination(response.data.meta || { current_page: entryPage, total_pages: 1, total_count: 0, per_page: 25 })
      })
      .catch((error) => {
        if (requestId !== latestEntriesRequestRef.current) return
        pageUtils.setApiErrorMsg(error.response?.data)
        pageUtils.setAlertMsg('Unable to fetch stock opname entries', ALERT_TYPES.DANGER)
      })
      .finally(() => {
        if (requestId !== latestEntriesRequestRef.current) return
        setIsLoading(false)
      })
  }

  const handleHeaderFieldChange = (event) => {
    const { name, type, checked, value } = event.target

    setHeaderValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleToggleInternalCategory = (key) => {
    setHeaderValues((prev) => {
      const currentFilters = normalizeInternalCategoryFilters(prev.internal_category_filter)
      const nextFilters = currentFilters.includes(key)
        ? currentFilters.filter((item) => item !== key)
        : [ ...currentFilters, key ]

      return {
        ...prev,
        internal_category_filter: (nextFilters.length ? nextFilters : currentFilters).join(',')
      }
    })
  }

  const handleSaveHeader = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (isSavingHeader) return

    setIsSavingHeader(true)

    const wasInProgress = !!stockOpname?.in_progress
    const hasEntries = Number(stockOpname?.total_entries || 0) > 0
    const values = hasEntries
      ? {
          title       : headerValues.title,
          in_progress : headerValues.in_progress
        }
      : {
          ...headerValues,
          target_date: headerValues.target_date || null
        }

    await StockOpnamesApiV2.update({
      id,
      values
    })
      .then((response) => {
        const nextStockOpname = response?.data?.stock_opname || null

        if (!nextStockOpname) {
          fetchStockOpname()
          fetchEntries({ q: query, scope: entryScope })
          pageUtils?.setAlertMsg?.('Stock Opname has been updated.', ALERT_TYPES.SUCCESS)
          return
        }

        setStockOpname(nextStockOpname)
        setHeaderValues({
          title                    : nextStockOpname.title || '',
          in_progress              : !!nextStockOpname.in_progress,
          target_date              : nextStockOpname.target_date || '',
          internal_category_filter : nextStockOpname.internal_category_filter || 'H,W,C',
          auto_generate_entries    : !!nextStockOpname.auto_generate_entries
        })

        if (wasInProgress && !nextStockOpname.in_progress) {
          pageUtils?.setAlertMsg?.('Stock Opname has been updated. Redirecting to All list.', ALERT_TYPES.SUCCESS)
          window.location.replace('/app/stock-opnames/list-page?scope=all')
          return
        }

        fetchEntries({ q: query, scope: entryScope })
        pageUtils?.setAlertMsg?.('Stock Opname has been updated.', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.(error?.response?.data?.messages?.base || 'Unable to update stock opname', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsSavingHeader(false))
  }

  const handleGenerateEntries = async () => {
    if (isGeneratingEntries) return

    setIsGeneratingEntries(true)

    await StockOpnamesApiV2.generateEntries(id)
      .then((response) => {
        const nextStockOpname = response?.data?.stock_opname || null
        const generationResult = response?.data?.generation_result || {}
        setStockOpname(nextStockOpname)
        fetchEntries({ q: query, scope: entryScope })
        pageUtils?.setAlertMsg?.(`${generationResult.created_count || 0} physical inventory (IQ > 0) entries generated. ${generationResult.skipped_count || 0} skipped.`, ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.(error?.response?.data?.messages?.base || 'Unable to generate stock opname entries', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsGeneratingEntries(false))
  }

  const handleBulkAddSku = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (isAddingSku) return

    setIsAddingSku(true)

    await StockOpnamesApiV2.bulkAddEntries({
      id,
      skuList: skuText
    })
      .then((response) => {
        const nextStockOpname = response?.data?.stock_opname || null
        setStockOpname(nextStockOpname)
        setSkuText('')
        fetchEntries({ q: query, scope: entryScope })

        const createdCount = response?.data?.created_count || 0
        const duplicateSkus = response?.data?.duplicate_skus || []
        const missingSkus = response?.data?.missing_skus || []

        const parts = [`${createdCount} SKU berhasil ditambahkan.`]
        if (duplicateSkus.length) parts.push(`Duplicate: ${duplicateSkus.join(', ')}`)
        if (missingSkus.length) parts.push(`Missing: ${missingSkus.join(', ')}`)

        pageUtils?.setAlertMsg?.(parts.join(' '), ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.(error?.response?.data?.messages?.base || 'Unable to add SKU entries', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsAddingSku(false))
  }

  const handleDownloadExcel = async () => {
    await StockOpnamesApiV2.downloadExcel(id)
      .then((response) => {
        const contentDisposition = response?.headers?.['content-disposition'] || ''
        const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
        const filename = filenameMatch?.[1] || `${stockOpname?.title || `stock_opname_${id}`}.xlsx`

        saveAs(response.data, filename)
      })
      .catch((error) => {
        pageUtils?.setApiErrorMsg?.(error?.response?.data)
        pageUtils?.setAlertMsg?.('Unable to download stock opname excel', ALERT_TYPES.DANGER)
      })
  }

  useEffect(() => {
    fetchStockOpname()
  }, [ id ]) // eslint-disable-line react-hooks/exhaustive-deps

  const requestedReturnTo = new URLSearchParams(location.search).get('return_to')

  useEffect(() => {
    const nextUrl = stockOpnameDetailPath({
      id,
      query,
      entryScope,
      entryPage,
      returnTo: requestedReturnTo
    })

    window.history.replaceState({}, '', nextUrl)
  }, [ id, query, entryScope, entryPage, requestedReturnTo ])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchEntries({ q: query, scope: entryScope, page: entryPage, per_page: 25 })
    }, 150)

    return () => clearTimeout(timeoutId)
  }, [ id, query, entryScope, entryPage ]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectedInternalCategoryFilters = normalizeInternalCategoryFilters(headerValues.internal_category_filter)
  const hasStockOpnameEntries = Number(stockOpname?.total_entries || 0) > 0
  const entryScopeCount = (scope) => {
    if (scope === 'cek_fisik') return stockOpname?.total_need_cek_fisik || 0
    if (scope === 'cek_kelengkapan') return stockOpname?.total_need_cek_kelengkapan || 0

    return stockOpname?.total_entries || 0
  }

  const openEntry = (entry) => {
    const returnTo = stockOpnameDetailPath({
      id,
      query,
      entryScope,
      entryPage,
      returnTo: requestedReturnTo
    })

    history.push(`/app/stock-opname-entries/${entry.id}/edit-page?return_to=${encodeURIComponent(returnTo)}`)
  }

  const fallbackBackPath = stockOpname?.in_progress ? '/app/stock-opnames/list-page' : '/app/stock-opnames/list-page?scope=all'
  const backPath = requestedReturnTo?.startsWith('/app/stock-opnames/list-page') ? requestedReturnTo : fallbackBackPath

  return (
    <>
      <TitlePage mainTitle={ 'Stock Opname' } subTitle={ 'Detail Page' } />

      <div className='d-flex justify-content-between align-items-center mb-3'>
        <RedirectButton
          color='secondary'
          size='sm'
          text='Back'
          path={ backPath }
        />
        <Button
          color='success'
          type='submit'
          form='stock-opname-header-form'
          disabled={ isSavingHeader || isLoadingHeader || !stockOpname }
        >
          { isSavingHeader ? 'Saving...' : 'Save Stock Opname' }
        </Button>
      </div>

      <Card className='mb-3'>
        <CardBody>
          {
            isLoadingHeader
              ? <Spinner color='primary' />
              : stockOpname ? (
                <Row>
                  <Col md={ 4 }>
                    <div><strong>{ 'Title: ' }</strong>{ stockOpname.title }</div>
                    <div><strong>{ 'Status: ' }</strong>{ stockOpname.in_progress ? 'In Progress' : 'Closed' }</div>
                    <div><strong>{ 'Target Date: ' }</strong>{ stockOpname.target_date || '-' }</div>
                    <div><strong>{ 'Filter: ' }</strong>{ stockOpname.internal_category_filter_label || '-' }</div>
                  </Col>
                  <Col md={ 8 }>
                    <Row className='mb-2'>
                      <Col xs={ 6 } md={ 4 }><strong>{ 'Total IQ' }</strong><div>{ stockOpname.total_inventory_quantity || 0 }</div></Col>
                      <Col xs={ 6 } md={ 4 }><strong>{ 'Actual Qty' }</strong><div>{ stockOpname.total_actual_quantity || 0 }</div></Col>
                      <Col xs={ 6 } md={ 4 }><strong>{ 'Correction' }</strong><div>{ stockOpname.total_needs_correction || 0 }</div></Col>
                    </Row>
                    <Row>
                      <Col md={ 4 }><strong>{ 'Cek Fisik' }</strong><div>{ progressLabel(stockOpname.total_physical_checked ?? stockOpname.total_product_found, stockOpname.total_entries) }</div></Col>
                      <Col md={ 4 }><strong>{ 'Cek Kelengkapan' }</strong><div>{ progressLabel(stockOpname.total_all_inclusions_found, stockOpname.total_entries) }</div></Col>
                      <Col md={ 4 }><strong>{ 'Price Tag' }</strong><div>{ progressLabel(stockOpname.total_price_tag_exist, stockOpname.total_entries) }</div></Col>
                    </Row>
                  </Col>
                </Row>
              ) : (
                <div>{ 'Stock opname not found' }</div>
              )
          }
        </CardBody>
      </Card>

      <Card className='mb-3'>
        <CardBody>
          <Form id='stock-opname-header-form' onSubmit={ handleSaveHeader }>
            <Row>
              <Col md={ 4 }>
                <FormGroup>
                  <Label>{ 'Title' }</Label>
                  <Input
                    name='title'
                    value={ headerValues.title || '' }
                    onChange={ handleHeaderFieldChange }
                    placeholder='Input stock opname title'
                  />
                </FormGroup>
              </Col>
              <Col md={ 3 }>
                <FormGroup>
                  <Label>{ 'Generate stock sampai tanggal' }</Label>
                  <Input
                    name='target_date'
                    type='date'
                    value={ headerValues.target_date || '' }
                    onChange={ handleHeaderFieldChange }
                    disabled={ hasStockOpnameEntries }
                  />
                </FormGroup>
              </Col>
              <Col md={ 3 }>
                <Label>{ 'Filter Internal Category' }</Label>
                <Row>
                  {
                    INTERNAL_CATEGORY_OPTIONS.map((option) => (
                      <Col md={ 4 } key={ option.key }>
                        <FormGroup check className='mb-2'>
                          <Label check>
                            <Input
                              type='checkbox'
                              checked={ selectedInternalCategoryFilters.includes(option.key) }
                              onChange={ () => handleToggleInternalCategory(option.key) }
                              disabled={ hasStockOpnameEntries }
                            />
                            { ` ${option.key}` }
                          </Label>
                        </FormGroup>
                      </Col>
                    ))
                  }
                </Row>
              </Col>
              <Col md={ 2 }>
                <FormGroup check style={{ marginTop: '38px' }}>
                  <Label check>
                    <Input
                      name='in_progress'
                      type='checkbox'
                      checked={ !!headerValues.in_progress }
                      onChange={ handleHeaderFieldChange }
                    />
                    { ' In Progress' }
                  </Label>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={ 4 }>
                {
                  hasStockOpnameEntries ? (
                    <small className='text-muted'>
                      { 'Generate settings dikunci karena Stock Opname sudah memiliki SKU entries. Title dan In Progress tetap dapat diubah.' }
                    </small>
                  ) : (
                    <FormGroup check className='mb-3'>
                      <Label check>
                        <Input
                          name='auto_generate_entries'
                          type='checkbox'
                          checked={ !!headerValues.auto_generate_entries }
                          onChange={ handleHeaderFieldChange }
                        />
                        { ' Auto-generate physical inventory entries (IQ > 0) on create' }
                      </Label>
                    </FormGroup>
                  )
                }
              </Col>
              <Col md={ 8 } className='text-right'>
                {
                  !hasStockOpnameEntries ? (
                    <Button
                      color='info'
                      type='button'
                      onClick={ handleGenerateEntries }
                      disabled={ isGeneratingEntries || !stockOpname?.in_progress }
                      style={{ marginRight: '8px' }}
                    >
                      { isGeneratingEntries ? 'Generating...' : 'Generate Physical Inventory Entries' }
                    </Button>
                  ) : null
                }
                <Button
                  color='success'
                  type='button'
                  onClick={ handleDownloadExcel }
                >
                  { 'Download Excel' }
                </Button>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      <Card className='mb-3'>
        <CardBody>
          <Form onSubmit={ handleBulkAddSku }>
            <FormGroup>
              <Label>{ 'Tambahkan SKU Entries' }</Label>
              <Input
                type='textarea'
                rows='5'
                value={ skuText }
                onChange={ (event) => setSkuText(event.target.value) }
                placeholder={ 'Masukkan 1 atau banyak SKU. Pisahkan dengan baris baru.' }
              />
              <small className='text-muted'>
                { 'Pisahkan SKU produk dengan Enter. Bisa ditambahkan selama Stock Opname masih In Progress.' }
              </small>
            </FormGroup>
            <Button
              color='primary'
              type='submit'
              disabled={ isAddingSku || !stockOpname?.in_progress }
            >
              { isAddingSku ? 'Adding...' : 'Tambahkan' }
            </Button>
          </Form>
        </CardBody>
      </Card>

      <Row className='mb-3'>
        <Col md={ 8 }>
          <ButtonGroup>
            {
              ENTRY_SCOPE_OPTIONS.map((option) => (
                <Button
                  key={ option.key }
                  color={ entryScope === option.key ? 'primary' : 'secondary' }
                  outline={ entryScope !== option.key }
                  onClick={ () => { setEntryScope(option.key); setEntryPage(1) } }
                >
                  { `${option.label} (${entryScopeCount(option.key)})` }
                </Button>
              ))
            }
          </ButtonGroup>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <Row className='mb-3'>
            <Col md={ 4 }>
              <InputGroup>
                <Input
                  value={ query }
                  onChange={ (event) => { setQuery(event.target.value); setEntryPage(1) } }
                  placeholder='Search entry by product / sku / supplier'
                />
                { query ? (
                  <InputGroupAddon addonType='append'>
                    <Button
                      color='secondary'
                      outline
                      onClick={ () => { setQuery(''); setEntryPage(1) } }
                      aria-label='Clear search'
                      title='Clear search'
                    >
                      { '×' }
                    </Button>
                  </InputGroupAddon>
                ) : null }
              </InputGroup>
            </Col>
          </Row>

          {
            isLoading && stockOpnameEntries.length === 0
              ? <Spinner color='primary' />
              : (
                <>
                  <div className='d-none d-md-block'>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th>{ 'SKU' }</th>
                          <th style={{ maxWidth: '300px' }}>{ 'Product Name' }</th>
                          <th>{ 'Inventory' }</th>
                          <th>{ 'SQ' }</th>
                          <th>{ 'IQ' }</th>
                          <th>{ 'Inventory Location' }</th>
                          <th>{ 'Supplier' }</th>
                          <th>{ 'Cek Fisik' }</th>
                          <th>{ 'Cek Kelengkapan' }</th>
                          <th>{ 'Price Tag' }</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          stockOpnameEntries.map((entry) => (
                            <tr
                              key={ entry.id }
                              onClick={ () => openEntry(entry) }
                              style={{ cursor: 'pointer' }}
                            >
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.stock_code || '-' }</td>
                              <td style={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.product_name || '-' }</td>
                              <td>{ entry.inventory_id || '-' }</td>
                              <td>{ entry.current_sellable_quantity ?? '-' }</td>
                              <td>{ entry.current_inventory_quantity ?? '-' }</td>
                              <td style={{ maxWidth: '260px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.current_inventory_location_display || '-' }</td>
                              <td style={{ maxWidth: '220px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{ entry.supplier || '-' }</td>
                              <td>{ physicalCheckBadge(entry) }</td>
                              <td>{ booleanBadge(entry.all_inclusions_found) }</td>
                              <td>{ booleanBadge(entry.price_tag_exist) }</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </Table>
                  </div>

                  <div className='d-md-none'>
                    {
                      stockOpnameEntries.map((entry) => (
                        <Card
                          key={ entry.id }
                          className='mb-2'
                          onClick={ () => openEntry(entry) }
                          style={{ cursor: 'pointer' }}
                        >
                          <CardBody className='py-3'>
                            <div><strong>{ entry.stock_code || '-' }</strong></div>
                            <div className='mb-2' style={{ wordBreak: 'break-word' }}>{ entry.product_name || '-' }</div>
                            <div>{ `Inventory: ${entry.inventory_id || '-'} · SQ: ${entry.current_sellable_quantity ?? '-'} · IQ: ${entry.current_inventory_quantity ?? '-'}` }</div>
                            <div style={{ wordBreak: 'break-word' }}>{ `Location: ${entry.current_inventory_location_display || '-'}` }</div>
                            <div style={{ wordBreak: 'break-word' }}>{ `Supplier: ${entry.supplier || '-'}` }</div>
                            <div className='mt-2'>
                              <span className='mr-2'>{ physicalCheckBadge(entry) }</span>
                              <span className='mr-2'>{ 'Kelengkapan ' }{ booleanBadge(entry.all_inclusions_found) }</span>
                              <span>{ 'Price Tag ' }{ booleanBadge(entry.price_tag_exist) }</span>
                            </div>
                          </CardBody>
                        </Card>
                      ))
                    }
                  </div>
                </>
              )
          }

          {
            isLoading && stockOpnameEntries.length > 0 ? (
              <div className='mb-2'><small className='text-muted'>{ 'Updating results...' }</small></div>
            ) : null
          }

          {
            (!isLoading || stockOpnameEntries.length > 0) && entryPagination?.total_pages > 1 ? (
              <div className='d-flex justify-content-center mt-3'>
                <PaginationBar
                  pagination={ entryPagination }
                  onClickPage={ (page) => setEntryPage(page) }
                />
              </div>
            ) : null
          }
        </CardBody>
      </Card>
    </>
  )
}

StockOpnameDetailPage.propTypes = propTypes

export default StockOpnameDetailPage
